import { useCallback, useState } from 'react';

import { PrescriptionHistoryDailyGroup } from '@dreryk/egabinet-prescription-frontend-api';

import { PrescriptionHistorySelection } from './PatientPrescriptionHistoryTable.types';
import {
   getSelectablePrescriptionIds,
   reconcileSelectedPrescriptionIds,
} from './PatientPrescriptionHistoryTable.utils';

export type PrescriptionSelection = PrescriptionHistorySelection & {
   readonly selectedPrescriptionIds: ReadonlySet<number>;
   readonly reconcileSelection: (dailyGroups: PrescriptionHistoryDailyGroup[]) => void;
};

export const usePrescriptionSelection = (): PrescriptionSelection => {
   const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

   const isPrescriptionSelected = (prescriptionId: number): boolean => selectedIds.has(prescriptionId);

   const isAllSelectedForDay = (dailyGroup: PrescriptionHistoryDailyGroup): boolean => {
      const selectable = getSelectablePrescriptionIds(dailyGroup);
      return selectable.length > 0 && selectable.every((id) => selectedIds.has(id));
   };

   const togglePrescription = useCallback((prescriptionId: number): void => {
      setSelectedIds((prev) => {
         const next = new Set(prev);
         if (next.has(prescriptionId)) {
            next.delete(prescriptionId);
         } else {
            next.add(prescriptionId);
         }
         return next;
      });
   }, []);

   const toggleAllForDay = useCallback((dailyGroup: PrescriptionHistoryDailyGroup): void => {
      setSelectedIds((prev) => {
         const next = new Set(prev);
         const selectable = getSelectablePrescriptionIds(dailyGroup);
         if (!selectable.length) {
            return next;
         }
         const allSelected = selectable.every((id) => next.has(id));
         if (allSelected) {
            selectable.forEach((id) => next.delete(id));
         } else {
            selectable.forEach((id) => next.add(id));
         }
         return next;
      });
   }, []);

   const reconcileSelection = useCallback((dailyGroups: PrescriptionHistoryDailyGroup[]): void => {
      setSelectedIds((prev) => reconcileSelectedPrescriptionIds(prev, dailyGroups));
   }, []);

   return {
      selectedPrescriptionIds: selectedIds,
      isPrescriptionSelected: isPrescriptionSelected,
      isAllSelectedForDay: isAllSelectedForDay,
      togglePrescription: togglePrescription,
      toggleAllForDay: toggleAllForDay,
      reconcileSelection: reconcileSelection,
   };
};
